<template>
  <div class="ladder-reward">
    <div class="ladders" ref='ladders' @scroll="onScroll">
      <div class="ladder" v-for="(ladder, index) in showLadders" :key="index">
        <div class="process-bar" :class="{'first-bar': ladder.isZeroPoint}">
          <div :class="['process', {'process-transition': ladder.needAnimation}]" :style="{'width': ladder.currentNodeFinishRate}"></div>
          <!-- 初始定位器 & 游标tip -->
          <div class="locator" ref="baseLocator" v-if="index === 1"></div>
          <template v-if="closestLevel === index || (currentProcess === 0 && index === 0) || (allFinished && index === showLadders.length -1)">
            <div class="locator" ref="locator"></div>
            <div class="vernier-wrap" :style="vernierStyle">
              <transition name="vernier">
                <div class="vernier"  v-if="showVernier && !['0%', '100%'].includes(closestLevelWidth)">
                  <span>{{ladder.vernierText}}</span>
                  <div class="triangle-radius"></div>
                </div>
              </transition>
            </div>
          </template>
        </div>
        <!-- 节点：初始/中间态(礼盒)/完成 -->
        <div :class="['node', {'lastest-node': ladder.isEndPoint}]" @click="onNodeClicked(index)">
          <div :class="['node-icon', {'zeroPoint': ladder.isZeroPoint, 'zeroCompleted': ladder.achieved}]"></div>
          <div :class="['node-info', {'highlight': ladder.achieved}]" v-show="ladder.ladder">
            <p class="node-required">{{ladder.nodeRequired}}</p>
            <p class="node-desc">{{ladder.ladderDesc}}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="transparent-mask mask-left"></div>
    <div class="transparent-mask mask-right"></div>
  </div>
</template>

<script>
import { sprintf } from '@didi/gettext'
import trackEvent from '@/common/js/track'
export default {
  props: {
    processData: {
      type: Array,
      default () {
        return []
      }
    },
    currentProcess: {
      type: Number,
      default: 0
    },
    activityID: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      allFinished: false,
      closestLevel: 0,
      // 计算得出的接近节点的宽度
      closestLevelWidth: '',
      showVernier: false, // 是否展示游标
      vernierStyle: {}, // 控制游标样式
      scrollTimer: '' // 定时器timer
    }
  },
  computed: {
    showLadders () {
      this.closestLevel = 0
      // 阶梯补充零点
      let ladderData = [{ladder: 0}, ...this.processData]
      // 判断是否已完成全部阶梯奖励
      this.allFinished = this.currentProcess >= ladderData[ladderData.length - 1].ladder
      // 未完成全部阶梯奖励时判断最接近完成的节点的index
      if (!this.allFinished) {
        ladderData.some((node, index) => {
          if (node.ladder > this.currentProcess) {
            this.closestLevel = index
            return true
          }
        })
      } else {
        this.closestLevel = ladderData.length - 1
      }
      // 遍历ladder处理&设置数据并返回可渲染的节点信息
      return ladderData.map((node, index) => {
        // 计算各节点完成进度
        let finishedPercent = 0
        if (this.allFinished) {
          finishedPercent = 1
        } else {
          // 如果一个节点都没有完成，则进度置0，无需计算
          if (this.closestLevel === 0) {
            finishedPercent = 0
          } else {
            if (index === this.closestLevel) {
              finishedPercent = (this.currentProcess - ladderData[index - 1].ladder) / (node.ladder - ladderData[index - 1].ladder)
            } else {
              finishedPercent = index > this.closestLevel ? 0 : 1
            }
          }
        }
        // 记录最接近完成节点宽度信息并在合适的时机设置
        if (this.closestLevel === index) {
          this.closestLevelWidth = `${finishedPercent * 100}%`
        }
        // 转化为可渲染的节点数据
        return {
          ...node,
          isZeroPoint: index === 0, // 是否为零点
          isEndPoint: index === ladderData.length - 1, // 是否为重点
          // 当前最接近完成的节点和前置节点都需要动画以及过度
          needAnimation: this.closestLevel === index || this.closestLevel === index + 1,
          isPrePosiion: this.closestLevel === index + 1,
          isCloseset: this.closestLevel === index,
          // 不对接近完成的节点及前置节点赋值，以便后续动画赋值
          currentNodeFinishRate: (index === this.closestLevel || index + 1 === this.closestLevel)
            ? ''
            : `${finishedPercent * 100}%`,
          achieved: index === 0 ? this.currentProcess > 0
            : this.closestLevel > index || this.allFinished, // 当前节点是否完成
          nodeRequired: sprintf(this.ngettext('%s 人', '%s 人', node.ladder), node.ladder), // 节点要求
          vernierText: sprintf(this.ngettext('%s 人', '%s 人', this.currentProcess), this.currentProcess) // 当前process文案
        }
      })
    }
  },
  watch: {
  },
  methods: {
    // 设置阶梯奖励的滚动位置
    setLaddersScroll () {
      // 不能使用scrollIntoView  因为低版本的苹果手机不支持参数设置
      // this.$refs.locator[0].scrollIntoView({block: 'end', inline: 'center'})
      let locatorLeft = this.$refs.locator[0].getBoundingClientRect().left
      let baseLocatorLeft = this.$refs.baseLocator[0].getBoundingClientRect().left
      this.$refs.ladders.scrollLeft = locatorLeft - baseLocatorLeft
    },
    // 设置游标样式
    setVernierStyle () {
      if (this.closestLevelWidth === '0%') {
        this.vernierStyle = {
          left: '-26px', // 21(icon width) + 5 (margin)
          transform: 'translateX(-50%)'
        }
      } else if (this.closestLevelWidth === '100%') {
        this.vernierStyle = {
          right: '-26px',
          transform: 'translateX(50%)'
        }
      } else {
        this.vernierStyle = {
          left: this.closestLevelWidth,
          transform: 'translateX(-50%)'
        }
      }
    },
    // 动画与过度的启动函数
    async startAnimation () {
      // 判断是否有已到达的节点, 无已达到的节点则退出动画执行函数
      if (!this.closestLevel) {
        return false
      }
      // 当前置节点为零点时 无需动画与过度
      if (this.closestLevel > 1) {
        // 前置节点过度
        await this.setBarWidthWith(document.querySelectorAll('.process')[this.closestLevel - 1], '100%')
        // 前置节点动画
        // await this.setNodeShakeAnimation(document.querySelectorAll('.node-icon')[this.closestLevel - 1])
      }
      // 接近节点过度
      if (this.closestLevelWidth !== '0%') {
        await this.setBarWidthWith(document.querySelectorAll('.process')[this.closestLevel], this.closestLevelWidth)
      }
      // 当所有节点都完成时，最后一个节点也需要触发动画
      if (this.allFinished) {
        // await this.setNodeShakeAnimation(document.querySelectorAll('.node-icon')[this.closestLevel])
      }
      // 游标的展示动画
      await this.setShowVernier()
    },
    // 设置节点的bar width，并触发过度
    setBarWidthWith (ele, width) {
      ele.style.width = width
      return new Promise(resolve => {
        ele.addEventListener('transitionend', function () {
          resolve()
        })
      })
    },
    // 设置节点的shake动画
    setNodeShakeAnimation (ele) {
      ele.className += ' shakeAnimation'
      return new Promise(resolve => {
        ele.addEventListener('animationend', function () {
          resolve()
        })
      })
    },
    // 展示游标并通过transition内置组件启动对应动画
    setShowVernier () {
      this.showVernier = true
    },
    // 节点点击埋点
    onNodeClicked (index) {
      try {
        trackEvent({
          eventId: 'sailing_c_x_cgc_ladder_reward_ck',
          eventLabel: '点击阶梯奖励节点',
          attrs: {
            activity_id: this.activityID,
            index: index
          }
        })
      } catch (e) {}
    },
    // 滚动事件埋点上报, 防抖
    onScroll () {
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => {
        try {
          trackEvent({
            eventId: 'sailing_c_x_cgc_ladder_reward_drag_ck',
            eventLabel: '拖拽阶梯奖励模块',
            attrs: {
              activity_id: this.activityID
            }
          })
        } catch (e) {}
      }, 300)
    }
  },
  mounted () {
    this.setLaddersScroll()
    // 动画和过度的启动函数包在setTime中，保证数据变动导致的渲染已经渲染完成
    setTimeout(() => {
      this.setVernierStyle()
      this.startAnimation()
    }, 500)
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/common/stylus/mixin.styl'
@import '~@/common/stylus/variables.styl'
.ladder-reward {
  overflow hidden
  position relative
  .ladders {
    // width 100%
    height 252px
    display flex
    align-items center
    justify-content flex-start
    box-sizing border-box
    overflow-x scroll
    overflow-y hidden
    // 滚动条撑开 隐藏滚动条
    margin-top 2px;
    padding-bottom 20px
    margin-bottom -20px
  }
  .ladder {
    flex none
    display flex
    justify-content flex-start
    align-items center
    margin-right 14px
    position relative
  }
  .process-bar {
    // width calc(60vw - 178px)
    width 160px
    height 12px
    border-radius 8px
    position relative
    background: #ececec
    .process {
      height 100%
      width 0%
      position absolute
      left 0
      top 0
      border-radius 8px
      background-primary-color()
    }
    .process-transition {
      transition width .3s linear 0s
    }
    .locator {
      position absolute
      left 50%
      height 0px
      width 1px
    }
    .vernier-wrap {
      position absolute
      top -58px
      .triangle-radius {
        width: 17px;
        height: 17px;
        border-radius: 3px;
        background-color: #ff7a45;
        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%) rotate(45deg);
        margin-top: -3px;
      }
    }
    .vernier {
      height 40px
      padding 0 12px
      background-primary-color()
      font-family 'Aspira Medium'
      font-size: 24px;
      color: #FFFFFF;
      font-weight: 500;
      line-height 40px
      border-radius 8px
      white-space nowrap
      // &::after {
      //   content ''
      //   display block
      //   border 17px solid transparent
      //   border-radius 3px
      //   border-top-primary-color()
      //   position absolute
      //   top 100%
      //   left 50%
      //   transform translateX(-50%)
      //   transform-origin center
      // }
    }
    .vernier-enter-active {
      transition all .2s ease-in-out
      transform-origin center
    }

    .vernier-leave-active {
      transition all .2s ease-in-out
      transform-origin center
    }
    .vernier-enter, .vernier-leave-to {
      transform scale(0.1)
      opacity .2
    }
    .vernier-enter-to, .vernier-leave {
      transform scale(1)
      opacity 1
    }
  }
  .first-bar {
    visibility hidden
    // width calc(15vw)
    width 88px
  }
  .placeholder-element {
    flex none
    width 200px
    height 12px
  }
  .node {
    width 88px
    height 88px
    position relative
    margin-left 12px
    .node-icon {
      width 88px
      height 88px
    }
    .node-info {
      width 90px
      height 52px
      padding-top 18px
      text-align center
      position absolute
      z-index 1
      top 100%
      left 50%
      transform translate(-50%)
      min-width 200px
      text-align center
      font-family 'Aspira Light'
      font-size 24px
      line-height 24px
      color #000000
      &.highlight {
        font-family 'Aspira Medium'
        font-size 26px
        line-height 30px
        height 62px
        font-color()
      }
      .node-required {
        height 24px
        white-space nowrap
        overflow hidden
        text-overflow ellipsis
        margin-bottom 4px
      }
      .node-desc {
        overflow hidden
        text-overflow ellipsis
        display -webkit-box
        -webkit-line-clamp 2
        -webkit-box-orient vertical
      }
    }
    .shakeAnimation {
      animation .2s ease-in-out 0s 2 shake;
      transform-origin center 70%
    }
    @keyframes shake {
      0% {
        transform rotate(0deg)
      }
      25% {
        transform rotate(9deg)
      }
      50% {
        transform rotate(0deg)
      }
      75% {
        transform rotate(-9deg)
      }
      100% {
        transform rotate(0deg)
      }
    }
  }
  .lastest-node {
    margin-right calc(17vw)
  }
  .transparent-mask {
    width 64px
    position absolute
    height 100%
    top 0
    z-index 2
    background-size 64px 100%
    background-position top center
    background-repeat no-repeat
  }
  .mask-left {
    left 0
    background-image url('../../../../assets/images/xtc/mask-left.png')
  }
  .mask-right {
    right 0
    background-image url('../../../../assets/images/xtc/mask-right.png')
  }
}
[data-theme="didi"] .node-icon {
  background url('../../../../assets/images/cgc/didi/reward_process_ladder.png') center/88px no-repeat
}
[data-theme="99"] .node-icon {
  background url('../../../../assets/images/cgc/99/reward_process_ladder.png') center/88px no-repeat
} 
[data-theme="didi"] .zeroPoint {
  background url('../../../../assets/images/cgc/didi/reward_process_one.png') center/88px no-repeat
}
[data-theme="99"] .zeroPoint {
  background url('../../../../assets/images/cgc/99/reward_process_one.png') center/88px no-repeat
}
[data-theme="didi"] .zeroCompleted {
  background url('../../../../assets/images/cgc/didi/reward_process_one_completed.png') center/88px no-repeat
}
[data-theme="99"] .zeroCompleted {
  background url('../../../../assets/images/cgc/didi/reward_process_one_completed.png') center/88px no-repeat
}
</style>